import React, { Component } from 'react';
import ValidatedInput from '../../Form/TextInput/ValidatedInput'
import Select from '../../Form/Select/Select';
import LivedAt from './LivedAt';
import FormStepLink from '../FormStep/FormStepLink';
import { emptyString, lowerCaseCompare } from '../../../utils/helpers';
import { gql } from "apollo-boost";
import { useApolloClient } from '@apollo/react-hooks';
import { postCodeTest } from '../../../utils/inputValidations'
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';

/** @import {ApolloQueryResult} from 'apollo-boost' */
/** @import {ApolloClient} from 'apollo-client' */
/** @import {Address, AddressResult, AddressesByPostcode, AutoEntryProps, AutoEntryState} from './AutoEntryTypes' */
/** @import { WithFeatureFlagsProps } from '../../FeatureFlags/FeatureFlagsTypes' */

/** @extends {Component<AutoEntryProps & WithFeatureFlagsProps & {client: ApolloClient<any>}, AutoEntryState>} */
class AutoEntry extends Component {
    /** @type {ApolloClient<any>} */
    apolloClient;

    /** @type {AutoEntryState} */
    state = {
        searching: false,
        postCode: this.props.fieldValues.postCode || '',
        addressResults: [],
        showValidationErrors: false
    };

    constructor(/** @type {AutoEntryProps & WithFeatureFlagsProps & {client: ApolloClient<any>}} */ props) {
        super(props);
        this.apolloClient = props.client;
    }

    componentDidMount() {
        if (this.props.fieldValues.postCode) {
            this.searchAddress(this.props.fieldValues.postCode);
        }
    }

    handleSetManualEntry = () => this.props.onSetManualEntry && this.props.onSetManualEntry(true);

    // for a given value, find the associated option object for react-select
    findOptionForValue = (/** @type {string} */ value, /** @type {AddressResult[]} */ results) => {
        const result = results.find(option => lowerCaseCompare(option?.value.toString() || "", value));
        return result && {label: result.label, value: result.value};
    }
        
    isValid = (/** @type {string} */ postCode) => {
        return postCodeTest(postCode);
    };

    validate = () => {
        this.setState({
            showValidationErrors: true // only start displaying validation errors after blurring
        });
    };

    formatAddress = (/** @type {Address} */ a, delim = ', ') => {
        return [
            a.flatNumber,
            a.buildingNumber,
            a.buildingName,
            a.streetLine1,
            a.streetLine2,
            a.town,
            a.postCode
        ].filter(s => !!s)
            .join(delim);
    };

    searchAddress = async (/** @type {string} */ postCode) => {
        this.setState({
            searching: true
        });

        /** @type {ApolloQueryResult<AddressesByPostcode>} */
        let results = await this.apolloClient.query({
            query: gql`
                query FindAddressesByPostcode($postcode: String!) {
                    addresses(postcode: $postcode) {
                        buildingName,
                        buildingNumber,
                        country,
                        flatNumber,
                        postCode,
                        streetLine1,
                        streetLine2,
                        town
                    }
                }
            `,
            variables: {
                postcode: postCode
            }
        });

        this.setState({
            searching: false,
            addressResults: results.data.addresses.map(addr => {
                const addressAsString = this.formatAddress(addr, '_');
                return {
                    address: addr,
                    label: this.formatAddress(addr),
                    value: addressAsString
                }
            })
        }, () => {
            if (this.props.onAddressResultsUpdated) {
                this.props.onAddressResultsUpdated(results.data.addresses);
            }
        });
    };

    handleSelectAddress = (/** @type {string} */ selectedValue) => {
        if (selectedValue) {
            let address = this.state.addressResults.find(a => a.value === selectedValue);
            if (address && this.props.onAddressSelected) {
                this.props.onAddressSelected(address);
            }
        } else {
            if (this.props.onAddressSelected) {
                this.props.onAddressSelected(); // clear address selected
            }
        }
    };

    handlePostCodeChanged = (/** @type {string} */ val) => {
        this.handleSelectAddress(); // clear address selected

        if (this.isValid(val)) {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            this.timeout = setTimeout(() => {
                this.searchAddress(val);
            }, 300);
        }

        this.setState({
            postCode: val,
            addressResults: []
        });
    };

    renderAddressSelect() {
        if (this.state.searching || emptyString(this.props.fieldValues.postCode || this.state.postCode)) {
            return (
                <>
                    <div className='form-step__row'>
                        <FormStepLink onClick={this.handleSetManualEntry}>
                            Enter manually
                        </FormStepLink>
                    </div>
                </>
            );
        }

        if (this.state.addressResults && this.state.addressResults.length > 0) {
            return (
                <>
                    <div className='form-step__row'>
                        <Select
                            name='address'
                            onChange={(option) => option && this.handleSelectAddress(option.value)}
                            placeholder='Select'
                            options={this.state.addressResults}
                            value={this.findOptionForValue(this.props.fieldValues.address, this.state.addressResults)}
                        />
                    </div>

                    <div className='form-step__row form-step__row__margin-top'>
                        <FormStepLink onClick={this.handleSetManualEntry}>
                            Enter manually
                        </FormStepLink>
                    </div>

                    {this.props.withLivedAt && (
                        <LivedAt
                            disabled={this.props.disabled}
                            // @ts-expect-error option / string are not the same; not sure how best to handle this 
                            onChange={val => this.props.handleUpdateField('livedAt')(val)}
                            livedAt={this.props.fieldValues.livedAt}
                        />
                    )}
                </>);
        }

        return (
            <>
                <div className='form-step__row'>
                    <FormStepLink onClick={this.handleSetManualEntry}>
                        Enter manually
                    </FormStepLink>
                </div>
            </>
        );
    }

    render() {
        let validationErrors = [];
        if (!!this.state.postCode && this.state.addressResults.length === 0 && !this.state.searching) {
            validationErrors.push({
                field: 'postCode',
                message: 'You must have a UK address to be able to apply'
            })
        }

        return (
            <>
                <div className='form-step__row'>
                    <div className="validated-input">
                        <ValidatedInput
                            onChange={this.handlePostCodeChanged}
                            name='postCode'
                            onBlur={this.validate}
                            placeholder='Post Code'
                            inputRef={this.props.inputRef}
                            value={this.state.postCode}
                            showRulesImmediately={false}
                            altErrorColor
                            onlyErrors
                            validationErrors={validationErrors}
                            showValidationErrors={this.state.showValidationErrors}
                            showLoading={this.state.searching} />
                    </div>
                </div>

                {this.renderAddressSelect()}
            </>
        );
    }
}

const AutoEntryWithApollo = (/** @type {AutoEntryProps & WithFeatureFlagsProps} */ props) => {
    const client = useApolloClient();
    return <AutoEntry {...props} client={client} />;
};

export default withFeatureFlags(AutoEntryWithApollo);
